import type { Translate } from 'next-translate';
import Trans from 'next-translate/Trans';
import Link from 'next/link';
import React from 'react';
import { type DropzoneOptions } from 'react-dropzone';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { Dropzone } from '@/components/landing/reusableSections';
import { getAcceptedTypes, niceBytes } from '@/helpers';
import { type Hero } from '@/types';
import { LANDING_CONSTANTS } from '@/constants';
import bgMobile from '@/public/images/landing/backgrounds/bg-abstract-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg-abstract.webp';
import styles from './HeroDefault.module.scss';

interface Props {
	data: Hero;
	t: Translate;
	dropzoneOptions: DropzoneOptions;
	isLoading: boolean;
	converterCompressor?: boolean;
}
export const HeroDefault: React.FC<Props> = ({
	data,
	t,
	dropzoneOptions,
	isLoading,
	converterCompressor,
}) => {
	// Destructuring
	const { title, description, buttonText, data: info, images } = data;

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const acceptedTypes =
		converterCompressor === true
			? LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE_CONVERTER_COMPRESSOR
			: LANDING_CONSTANTS.ACCEPTED_IMAGE_TYPES_DROPZONE;

	const accept = Object.fromEntries(
		Object.entries(acceptedTypes ?? {}).map(([key, value]) => [
			key,
			[...value],
		]),
	);
	const normalizeMaxSize = niceBytes({
		bytes: LANDING_CONSTANTS.MAX_FILE_SIZE ?? 0,
	});
	const normalizeTypes = getAcceptedTypes(Object.keys(accept));
	const leftImage = images.find(img => img.id === 'left');
	const rightImage = images.find(img => img.id === 'right');
	const isNoImage =
		typeof leftImage === 'string' || typeof rightImage === 'string';

	return (
		<section className={`${styles['hero']}`}>
			<MyImage
				imageProps={{
					src: bgMobile,
					alt: 'background image',
					title: `${t(title)}`,
					priority: true,
					fetchPriority: 'low',
				}}
				settings={{
					classes: {
						picture: `${styles['hero__background']}`,
						image: styles['hero__background-img'],
					},
				}}
			>
				<source srcSet={bg.src} media='(min-width: 1023px)' />
			</MyImage>
			<Container className={`${styles['hero__container']}`}>
				<div className={`${styles['hero__info']}`}>
					<h1 className={`${styles['hero__title']}`}>{t(title)}</h1>
					<p className={`${styles['hero__subtitle']}`}>{t(description)}</p>
				</div>

				<div className={`${styles['hero__dropzone-wrapper']}`}>
					<Dropzone
						isLoading={isLoading}
						title={t(info.dropzoneTitle)}
						buttonText={t(buttonText)}
						options={dropzoneOptions}
						classes={{
							dropzone: styles['hero__dropzone'],
							info: styles['hero__dropzone-info'],
						}}
						onErrorMessageChange={setErrorMessage}
					/>

					<div className={styles['hero__dropzone-mobile-info']}>
						{errorMessage && (
							<span
								className={`${styles['hero__dropzone-mobile-text']} ${styles['hero__dropzone-mobile-text_error']}`}
							>
								{errorMessage}
							</span>
						)}
						<div className={styles['hero__dropzone-mobile-info-container']}>
							<span className={styles['hero__dropzone-mobile-text']}>
								{normalizeTypes}
							</span>
							<span className={styles['hero__dropzone-mobile-text']}>
								Max - {normalizeMaxSize}
							</span>
						</div>
					</div>

					<span className={`${styles['hero__agree']}`}>
						<Trans
							i18nKey='common:agreeUploadFile'
							components={{
								terms: <Link href='/terms-of-use' target='_blank' />,
								privacy: <Link href='/privacy-policy' target='_blank' />,
							}}
						/>
					</span>

					<div
						className={`${styles['hero__images']} ${
							isNoImage ? styles['hero__images_noImage'] : ''
						}`}
					>
						{images[0].id === 'left' && typeof images[0].image !== 'string' ? (
							<MyImage
								imageProps={{
									src: images[0].image,
									alt: t(images[0].alt) ?? 'example photo edited with Lift',
									title: images[0].title,
									priority: true,
									fetchPriority: 'high',
								}}
								settings={{
									classes: {
										picture: `${styles['hero__picture']} ${styles['hero__picture_left']}`,
										image: styles['hero__image'],
									},
								}}
							/>
						) : null}

						{images[1].id === 'right' && typeof images[1].image !== 'string' ? (
							<MyImage
								imageProps={{
									src: images[1].image,
									alt: t(images[1].alt) ?? 'example photo edited with Lift',
									title: images[1].title,
									priority: true,
									fetchPriority: 'low',
								}}
								settings={{
									classes: {
										picture: `${styles['hero__picture']} ${styles['hero__picture_right']}`,
										image: styles['hero__image'],
									},
								}}
							/>
						) : null}
					</div>
				</div>
			</Container>
		</section>
	);
};
